import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import NoResults from "../../../../../components/NoResults/NoResults";
import SearchFilter from "../../../../../components/Filter/SearchFilter";
import useFetch from "../../../../../hooks/fetchMSSQL-hook";
import exportReportXlsx from "../../utils/exportReportXlsx";
import { getReportMachineInfo, matchesReportFilters, hasReportFilters, reportFilterOptions } from "../../utils/machineReportFilters";

const extractSerialFromFullName = (fullName) => {
    if (!fullName) return null;
    const m = String(fullName).match(/\(([^()]+)\)\s*$/);
    return m?.[1]?.trim() || null;
};

const getSerialFromMachine = (m) => {
    return (
        (m?.SerialNo && String(m.SerialNo).trim()) ||
        (m?.MachineSerialNo && String(m.MachineSerialNo).trim()) ||
        extractSerialFromFullName(m?.MachineFullName) ||
        null
    );
};

const getSerialFromRow = (r) => {
    return (
        (r?.MachineSerialNo && String(r.MachineSerialNo).trim()) ||
        (r?.SerialNo && String(r.SerialNo).trim()) ||
        extractSerialFromFullName(r?.MachineFullName) ||
        null
    );
};

const isMachineActive = (m) => {
    if (m?.IsActive === undefined || m?.IsActive === null) return true;
    if (typeof m.IsActive === "boolean") return m.IsActive;
    return Boolean(Number(m.IsActive));
};

const getValue = (obj, keys) => {
    for (const key of keys) {
        if (obj?.[key] !== undefined && obj?.[key] !== null && String(obj[key]).trim() !== "") {
            return String(obj[key]).trim();
        }
    }

    return null;
};

const getChoiceNo = (r) => {
    return getValue(r, [
        "MachineFeederNo",
        "FeederNo",
        "ChoiceNo",
        "SelectionNo",
        "MachineChoiceNo",
        "VendNo",
        "VMCChoiceNo",
    ]);
};

const getMDBCode = (r) => {
    return getValue(r, [
        "MDBCode",
        "MDB",
        "MDBNo",
        "MDBProductNo",
        "TrxProductNo",
        "ProductNo",
    ]);
};

const getMachineInfo = getReportMachineInfo;

const DEFAULT_REPORT_FILTER = {
    reportMinDays: 7,
    reportLimit: 100,
    reportMachine: "",
    machineTags: { id: [], name: [] },
    reportRegion: "",
    reportRoute: "",
};

const getMachineIdsForTags = (fetchMssqlApi, tagIds) => new Promise((resolve, reject) => {
    fetchMssqlApi(
        "tags/getMachineIdsByTags",
        { method: "POST", hideNotification: true, data: { machineTagIds: tagIds } },
        (data) => {
            if (!Array.isArray(data)) {
                reject(new Error("Nieprawidłowa odpowiedź podczas pobierania automatów według tagów"));
                return;
            }
            resolve(new Set(data.map(Number).filter(Number.isFinite)));
        },
        reject
    );
});

export default function ZeroSalesChart({ machines = [], products = [] }) {
    const { fetchMssqlApi } = useFetch();

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const exportPayloadRef = useRef(null);
    const requestIdRef = useRef(0);
    useEffect(() => () => { requestIdRef.current += 1; }, []);

    const machineInfoBySerial = useMemo(() => {
        const map = new Map();

        (machines || []).forEach((m) => {
            const serial = getSerialFromMachine(m);

            if (serial) {
                map.set(serial, getMachineInfo(m));
            }
        });

        return map;
    }, [machines]);

    const renderMachineInfo = (r) => {
        const serial = getSerialFromRow(r);
        const fromMachine = serial ? machineInfoBySerial.get(serial) : null;
        const fromRow = getMachineInfo(r);

        const tags = fromRow.tags || fromMachine?.tags;
        const region = fromRow.region || fromMachine?.region;
        const route = fromRow.route || fromMachine?.route;

        if (!tags && !region && !route) return "-";

        return (
            <div className="small">
                {tags && <div><span className="text-muted">Tagi: </span>{tags}</div>}
                {region && <div><span className="text-muted">Rejon: </span>{region}</div>}
                {route && <div><span className="text-muted">Trasa: </span>{route}</div>}
            </div>
        );
    };

    const machineNameToSerial = useMemo(() => {
        const map = new Map();
        (machines || []).forEach((m) => {
            const serial = getSerialFromMachine(m);
            if (!serial) return;
            map.set(String(serial).toLowerCase(), serial);
            const name = m?.Name ? String(m.Name).trim() : null;
            if (name) map.set(`${name} (${serial})`.toLowerCase(), serial);
            const fullName = m?.MachineFullName ? String(m.MachineFullName).trim() : null;
            if (fullName) map.set(fullName.toLowerCase(), serial);
        });
        return map;
    }, [machines]);

    const reportOptions = useMemo(() => ({
        machines: (machines || []).map((m) => {
            const serial = getSerialFromMachine(m);
            const name = m?.Name ? String(m.Name).trim() : null;
            return (name && serial && `${name} (${serial})`) || m?.MachineFullName || serial || null;
        }).filter(Boolean),
        regions: reportFilterOptions(machines, "region"),
        routes: reportFilterOptions(machines, "route"),
    }), [machines]);

    const allActiveSerials = useMemo(() => Array.from(new Set(
        (machines || []).filter(isMachineActive).map(getSerialFromMachine).filter(Boolean)
    )), [machines]);

    const productsByEAN = useMemo(() => {
        const map = new Map();
        (products || []).forEach((p) => {
            if (p?.EAN) {
            map.set(String(p.EAN), p.Name);
            }
        });
        return map;
    }, [products]);

    const getProductName = (row) => {
    if (row?.ProductName) return row.ProductName;

    if (row?.EAN) {
        return productsByEAN.get(String(row.EAN)) ?? `EAN: ${row.EAN}`;
    }

    return "-";
}


    const fetchZeroSales = useCallback((reportFilter = DEFAULT_REPORT_FILTER) => {
        const requestId = ++requestIdRef.current;
        exportPayloadRef.current = null;
        const machineValue = String(reportFilter.reportMachine || "").trim();
        const selectedSerial = machineValue ? machineNameToSerial.get(machineValue.toLowerCase()) : null;
        const metadataFilters = {
            region: String(reportFilter.reportRegion || ""),
            route: String(reportFilter.reportRoute || ""),
        };
        const tagIds = Array.isArray(reportFilter.machineTags?.id)
            ? [...new Set(reportFilter.machineTags.id.map(Number).filter(id => Number.isInteger(id) && id > 0))]
            : [];

        if (machineValue && !selectedSerial) {
            setRows([]);
            setLoading(false);
            return;
        }

        const daysNumber = Number(reportFilter.reportMinDays);
        const minDays = Number.isFinite(daysNumber) && daysNumber >= 1
            ? Math.min(365, Math.trunc(daysNumber)) : 7;
        const limitNumber = Number(reportFilter.reportLimit);
        const limit = Number.isFinite(limitNumber) && limitNumber >= 1
            ? Math.min(1000, Math.trunc(limitNumber)) : 100;

        const sendReport = (taggedMachineIds = null) => {
            if (requestId !== requestIdRef.current) return;
            const serialsToSend = (!selectedSerial && !hasReportFilters(metadataFilters) && !taggedMachineIds)
                ? allActiveSerials
                : Array.from(new Set((machines || [])
                    .filter(isMachineActive)
                    .filter(m => !selectedSerial || getSerialFromMachine(m) === selectedSerial)
                    .filter(m => matchesReportFilters(m, metadataFilters))
                    .filter(m => !taggedMachineIds || taggedMachineIds.has(Number(m?.MachineId)))
                    .map(getSerialFromMachine)
                    .filter(Boolean)));

            if (!serialsToSend.length) {
                setRows([]);
                setLoading(false);
                return;
            }

            const payload = { machineSerialNos: serialsToSend, minDays, limit };
            setLoading(true);
            fetchMssqlApi(
                "stat/zero-sales",
                {
                    method: "POST",
                    hideNotification: true,
                    showLoader: true,
                    data: payload,
                },
                (data) => {
                    if (requestId !== requestIdRef.current) return;
                    const arr = Array.isArray(data) ? data : [];
                    arr.sort((a, b) => {
                        const ta = new Date(a?.LastSaleDateTime || 0).getTime();
                        const tb = new Date(b?.LastSaleDateTime || 0).getTime();
                        return tb - ta;
                    });
                    setRows(arr);
                    exportPayloadRef.current = arr.length ? payload : null;
                    setLoading(false);
                },
                (err) => {
                    if (requestId !== requestIdRef.current) return;
                    console.log(err);
                    setRows([]);
                    setLoading(false);
                }
            );
        };

        if (tagIds.length > 0) {
            setLoading(true);
            getMachineIdsForTags(fetchMssqlApi, tagIds)
                .then(sendReport)
                .catch(err => {
                    if (requestId !== requestIdRef.current) return;
                    console.error("[ZeroSalesChart] Błąd filtrowania tagów automatów", err);
                    setRows([]);
                    setLoading(false);
                });
        } else {
            sendReport();
        }
    }, [fetchMssqlApi, machineNameToSerial, allActiveSerials, machines]);

    const formatLastSale = (dt) => {
        if (!dt) return "-";
        const m = moment(dt);
        if (!m.isValid()) return String(dt);
        const days = moment().diff(m, "days");
        return `${m.format("YYYY-MM-DD HH:mm:ss")} (${days} dni temu)`;
    };

    const exportToExcel = () => {
        if (loading || exporting || !exportPayloadRef.current) return;
        setExporting(true);
        exportReportXlsx(fetchMssqlApi, "stat/zero-sales/export", exportPayloadRef.current, () => setExporting(false));
    };

    const isEmpty = !rows || rows.length === 0;

return (
    <div className="m-2">
        <SearchFilter
            filterName="zeroSalesReport"
            defaultFilter={DEFAULT_REPORT_FILTER}
            search={fetchZeroSales}
            reportOptions={reportOptions}
            forNow
            toolbarAction={
                <button type="button" className="btn text-primary d-inline-flex align-items-center p-0" onClick={exportToExcel}
                    disabled={loading || exporting || isEmpty}>
                    <i className="fas fa-file-export mr-1" />Export
                </button>
            }
        />
    <div className="card overflow-auto">
        <div
        className="card-body overflow-auto p-0 custom-scroll border"
        style={{ maxHeight: window.innerHeight - 268, overflowY: "auto" }}
        >
        {isEmpty ? (
            <div className="p-3">
            <NoResults />
            </div>
        ) : (
            <table className="table table-striped mb-0">
            <thead>
                <tr className="headers-stick-trxs">
                <th className="p-2" style={{ minWidth: 220 }}>
                    Automat
                </th>
                <th className="p-2 text-center" style={{ width: 110 }}>
                    Nr wyboru
                </th>
                <th className="p-2 text-center" style={{ width: 110 }} title="To jest kod MDB, a nie numer wyboru z maszyny">
                    Kod MDB
                </th>
                <th className="p-2" style={{ minWidth: 220 }}>
                    Produkt
                </th>
                <th className="p-2" style={{ minWidth: 220 }}>
                    Tagi / Rejon / Trasa
                </th>
                <th className="p-2" style={{ minWidth: 210 }}>
                    Data ostatniej sprzedaży
                </th>
                </tr>
            </thead>
            <tbody>
                {rows.map((r, idx) => (
                <tr key={idx}>
                    <td className="p-2">
                    {r?.MachineName ||
                        r?.MachineFullName ||
                        r?.MachineSerialNo ||
                        "-"}
                    </td>
                    <td className="p-2 text-center">{getChoiceNo(r) ?? "-"}</td>
                    <td className="p-2 text-center">
                        {getMDBCode(r) ?? "-"}
                    </td>
                    <td className="p-2">{getProductName(r)}</td>
                    <td className="p-2">{renderMachineInfo(r)}</td>
                    <td className="p-2 text-nowrap">
                    {formatLastSale(r?.LastSaleDateTime)}
                    </td>
                </tr>
                ))}
            </tbody>
            </table>
        )}
        </div>


    </div>
    </div>
);
}